/**
 * Signup Page — Hero image layout (primary auth landing)
 *
 * Full-bleed background image, gradient overlay, buttons anchored bottom.
 * This is the first screen new users see.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { T, BRAND } from '@/styles/tokens';

const useFadeIn = (delay = 0) => {
  const [mounted, setMounted] = useState(false);
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    setTimeout(() => setMounted(true), 100 + delay);
  }, [delay]);

  return {
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(15px)',
    transition: prefersReducedMotion ? 'none' : 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  };
};

function getAuthErrorMessage(err: any): string {
  const code = err.code;
  switch (code) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists';
    case 'auth/invalid-email':
      return 'Invalid email address';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in cancelled';
    case 'auth/popup-blocked':
      return 'Pop-up blocked. Allow pop-ups and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email';
    case 'auth/invalid-api-key':
      return 'Configuration error. Contact support.';
    default:
      if (err.message?.includes('API key')) {
        return 'Configuration error. Contact support.';
      }
      return 'Account creation failed. Try again.';
  }
}

export function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signInWithGoogle, signInWithApple, profile } = useAuth();

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Preserve next param from URL (e.g., /signup?next=/app/calculate)
  const params = new URLSearchParams(location.search);
  const next = params.get('next') || '/app/dashboard';
  const nextRef = useRef(next);

  // Post-auth redirect — fires whenever profile becomes available.
  // Profile is only non-null for authenticated users, so this is the
  // single reliable signal for both flows:
  //   - Desktop popup: signInWithPopup resolves → loadProfile sets profile
  //   - Redirect flow: page reloads → getRedirectResult/onAuthStateChanged sets profile
  // RedirectIfAuthenticated (wrapper) also redirects, but it depends on
  // isAuthenticated (user object), which can lag behind profile on redirect returns.
  // This useEffect catches that gap.
  useEffect(() => {
    if (profile) {
      if (!profile.onboardingCompleted) {
        navigate(`/onboarding?next=${encodeURIComponent(nextRef.current)}`, { replace: true });
      } else {
        const postAuthAction = sessionStorage.getItem('postAuthAction');
        const destination = postAuthAction === 'save' ? '/app/result' : nextRef.current;
        if (postAuthAction) sessionStorage.removeItem('postAuthAction');
        navigate(destination, { replace: true });
      }
    }
  }, [profile, navigate]);

  const socialStyle = useFadeIn(450);
  const footerStyle = useFadeIn(750);

  const handleGoogleSignIn = async () => {
    console.warn('[SignupPage] handleGoogleSignIn fired, isLoading:', isLoading);
    setError('');
    setIsLoading(true);
    try {
      await signInWithGoogle();
      console.warn('[SignupPage] Google sign-in resolved OK');
      // On desktop popup: auth context updates → useEffect redirects
      // On mobile redirect: page reloads → handled on remount
    } catch (err: any) {
      const code = err?.code || 'unknown';
      const msg = err?.message || String(err);
      console.warn('[SignupPage] Google sign-in error:', code, msg, err);
      setError(import.meta.env.DEV ? `[${code}] ${msg}` : getAuthErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    console.warn('[SignupPage] handleAppleSignIn fired');
    setError('');
    setIsLoading(true);
    try {
      await signInWithApple();
      console.warn('[SignupPage] Apple sign-in resolved OK');
    } catch (err: any) {
      const code = err?.code || 'unknown';
      const msg = err?.message || String(err);
      console.warn('[SignupPage] Apple sign-in error:', code, msg, err);
      setError(import.meta.env.DEV ? `[${code}] ${msg}` : getAuthErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background image — fills entire viewport */}
      <img
        src="/brand/login-bg.png"
        alt=""
        role="presentation"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center top',
          zIndex: 0,
        }}
      />

      {/* Gradient overlay — transparent top, solid bottom for form area */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          background: `linear-gradient(to bottom, transparent 55%, rgba(250,248,243,0.5) 68%, rgba(250,248,243,0.92) 78%, ${BRAND.cream} 85%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Spacer — image fills most of viewport */}
      <div style={{ flex: 1 }} />

      {/* Bottom form area — compact, anchored */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          padding: '0 24px',
          paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
          maxWidth: 420,
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Error message */}
        {error && (
          <div
            style={{
              marginBottom: 12,
              padding: 10,
              backgroundColor: 'rgba(214,69,69,0.12)',
              borderRadius: 10,
              fontSize: 13,
              color: T.danger,
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}

        {/* Apple — primary CTA */}
        <div style={socialStyle}>
          <button
            onClick={handleAppleSignIn}
            disabled={isLoading}
            style={{
              width: '100%',
              height: 52,
              borderRadius: 14,
              border: 'none',
              backgroundColor: BRAND.ink,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              fontFamily: BRAND.fontSans,
              fontSize: 16,
              fontWeight: 600,
              color: T.white,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1,
              marginBottom: 10,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            Sign up with Apple
          </button>

          {/* Google — secondary, outlined */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            style={{
              width: '100%',
              height: 52,
              borderRadius: 14,
              border: '1.5px solid rgba(0,0,0,0.12)',
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              fontFamily: BRAND.fontSans,
              fontSize: 16,
              fontWeight: 600,
              color: BRAND.ink,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1,
              marginBottom: 10,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </button>
        </div>

        {/* Footer */}
        <p
          style={{
            ...footerStyle,
            textAlign: 'center',
            fontFamily: BRAND.fontSans,
            fontSize: 13,
            color: BRAND.inkLight,
            marginTop: 16,
            marginBottom: 0,
          }}
        >
          Already have an account?{' '}
          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontFamily: BRAND.fontSans,
              color: BRAND.teal,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Sign in &rarr;
          </button>
        </p>
      </div>
    </div>
  );
}

export default SignupPage;
